<template>
  <div>
    <e-filters
      :title="$t('Filtros')"
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="6">
          <e-store-combo
            id="store-id"
            v-model="filters.id"
            :required="false"
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            id="company-name"
            v-model="filters.companyName"
            type="text"
            :label="$t('Razão social')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="active"
            v-model="filters.active"
            type="vue-select"
            :label="$t('Status')"
            :placeholder="$t('Todos')"
            :options="activeInactiveTypes()"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="cnpj"
            v-model="filters.cnpj"
            type="text-mask"
            :mask="['XX.XXX.XXX/XXXX-##']"
            :label="$t('CNPJ')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="headId"
            v-model="filters.headId"
            type="vue-select"
            :label="$t('Head')"
            :options="headOptions"
            :placeholder="$t('Todos')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="headId"
            v-model="filters.directorId"
            type="vue-select"
            :label="$t('Diretor')"
            :options="directorOptions"
            :placeholder="$t('Todos')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="email"
            v-model="filters.email"
            type="text"
            :label="$t('E-mail')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="public-place"
            v-model="filters.publicPlace"
            type="text"
            :label="$t('Logradouro')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="type"
            v-model="filters.type"
            type="vue-select"
            :label="$t('Tipo')"
            :options="storeTypes()"
            :placeholder="$t('Selecione')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="businessModel"
            v-model="filters.businessModel"
            type="vue-select"
            :label="$t('Modelo')"
            :options="storeBusinessModels()"
            :placeholder="$t('Selecione')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="region"
            v-model="filters.region"
            type="vue-select"
            :label="$t('Região')"
            :options="storeRegions()"
            :placeholder="$t('Selecione')"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="province"
            v-model="filters.province"
            type="vue-select"
            :label="$t('Estado')"
            :options="localProvinces"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="city"
            v-model="filters.city"
            type="text"
            :label="$t('Cidade')"
          />
        </b-col>
      </b-row>
    </e-filters>
    <b-card-actions
      ref="table-card"
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-row>
        <b-col md="12">
          <b-table
            id="stores-table"
            ref="stores-table"
            class="table-responsive bordered"
            responsive
            striped
            show-empty
            :empty-text="getEmptyTableMessage($tc('STORE.NAME'), 'female', queryExecuted)"
            :items="stores"
            :fields="fields"
            no-local-sorting
            :sort-by.sync="sorting.sortBy"
            :sort-desc.sync="sorting.sortDesc"
            @sort-changed="getData"
          >
            <template #cell(actions)="row">
              <e-grid-actions
                :show-delete="false"
                :show-update="$can('Update', 'Store')"
                :buttons="deliveryButtons(row.item)"
                :max-inline-actions="3"
                @update="edit(row.item)"
                @toggle-delivery="toggleDelivery(row.item)"
                @edit-min-delivery-minutes="editMinDeliveryMinutes(row.item)"
              />
            </template>
            <template #cell(active)="{ value }">
              <e-status-badge :status="value" />
            </template>
          </b-table>
          <b-row>
            <b-col md="6">
              <e-pagination-summary
                :current-page="paging.currentPage"
                :per-page="paging.pageSize"
                :total="paging.rowCount"
                :total-on-page="paging.rowsInCurrentPage"
              />
            </b-col>
            <b-col md="6">
              <b-pagination
                v-model="paging.currentPage"
                align="right"
                :total-rows="paging.rowCount"
                :per-page="paging.pageSize"
                aria-controls="product-table"
                @change="pageChange"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card-actions>
    <FAB
      v-if="$can('Create', 'Store')"
      :main-tooltip="$t('Adicionar Loja')"
      @click="create"
    />

    <store-min-delivery-minutes-modal
      ref="minDeliveryMinutesModal"
      @saved="getData"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { BTable, BPagination, BRow, BCol } from 'bootstrap-vue'
import { FAB, EPageSizeSelector, EPaginationSummary, EFilters } from '@/views/components'

import { downloader, province, statusTypes, storeDomains, translate } from '@/mixins'
import EGridActions from '@/views/components/EGridActions.vue'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import StoreMinDeliveryMinutesModal from './components/StoreMinDeliveryMinutesModal.vue'

export default {
  components: {
    BCardActions,
    BTable,
    BPagination,
    BRow,
    BCol,
    EPageSizeSelector,
    EPaginationSummary,
    FAB,
    EFilters,
    EGridActions,
    EStatusBadge,
    EStoreCombo,
    StoreMinDeliveryMinutesModal,
  },
  mixins: [downloader, province, storeDomains, statusTypes, translate],
  data() {
    return {
      queryExecuted: false,
      fetching: false,
      loading: false,
      exporting: false,
      orderBy: {
        key: 'dadoContrato.dataAdesao',
        direction: 'desc',
      },
    }
  },
  computed: {
    ...mapGetters('app', ['statesWithStore']),
    ...mapGetters('pages/store/storeMaintain', ['headOptions', 'directorOptions']),
    ...mapState('pages/store/storeMaintain', {
      stores: 'stores',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),
    localProvinces() {
      return this.provinces().filter(
        provinceItem => this.statesWithStore.indexOf(provinceItem.value) >= 0
      )
    },
    rows() {
      return this.paging.totalRows || this.stores.length
    },
    fields() {
      return [
        {
          key: 'actions',
          label: this.$t('Ações'),
          class: 'text-center',
          thStyle: { width: '150px', minWidth: '150px' },
          tdClass: 'text-nowrap',
        },
        {
          key: 'cnpj',
          label: this.$t('Cnpj'),
          thStyle: 'width: 170px',
          formatter: value => this.$options.filters.cpfCnpj(value) || '-',
        },
        {
          key: 'tradingName',
          label: this.$t('Nome Fantasia'),
          formatter: (val, key, item) => this.$options.filters.storeName(item),
          sortable: true,
        },
        {
          key: 'type',
          label: this.$t('Tipo'),
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          formatter: value => this.storeTypesLabel[value] || '-',
          sortable: true,
        },
        {
          key: 'address.province',
          label: this.$t('Estado'),
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          formatter: value => value || '-',
          sortable: true,
        },
        {
          key: 'region',
          label: this.$t('Região'),
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          formatter: value => value || '-',
          sortable: true,
        },
        {
          key: 'address.city',
          label: this.$t('Cidade'),
          thStyle: { width: '300px' },
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '80px' },
          sortable: true,
        },
        {
          label: this.$t('Tempo delivery'),
          key: 'minDeliveryScheduleMinutes',
          class: 'text-center',
          thStyle: { width: '80px' },
          sortable: true,
        },
      ]
    },
  },
  mounted() {
    this.fetchHeads()
    this.fetchDirectors()
  },
  methods: {
    ...mapActions('pages/store/storeMaintain', [
      'fetchStores',
      'fetchHeads',
      'fetchDirectors',
      'resetFilters',
      'setCurrentPage',
      'setPageSize',
      'clearStore',
      'setStoreDeliveryPaused',
    ]),

    // Botões de delivery na coluna de ações. Só aparecem com permissão de editar e apenas para
    // lojas ativas (não faz sentido operar o delivery de loja inativa).
    // - Pausar/retomar: reusa o flag Store.Delivery (ligado ⇒ "Pausar"; desligado ⇒ "Retomar").
    //   Aparece nos dois estados, porque é o próprio botão que retoma o delivery pausado.
    // - Tempo mínimo: edição rápida de Store.MinDeliveryScheduleMinutes e de
    //   Store.LendingInstallationMinutes sem abrir a manutenção. Só com o delivery ligado — os
    //   campos não têm efeito em loja que não oferece entrega.
    deliveryButtons(store) {
      if (!this.$can('Update', 'Store') || store.active !== true) return []
      const active = store.delivery === true
      const buttons = [
        {
          event: 'toggle-delivery',
          icon: active ? 'pause-circle' : 'play-circle',
          variant: active ? 'outline-warning' : 'outline-success',
          title: active ? this.$t('Pausar delivery') : this.$t('Retomar delivery'),
        },
      ]

      if (active) {
        buttons.push({
          event: 'edit-min-delivery-minutes',
          icon: 'clock',
          variant: 'outline-primary',
          title: `${this.$t('Tempo mínimo de entrega')}: ${
            store.minDeliveryScheduleMinutes ?? '-'
          } min | ${this.$t('Tempo de instalação do comodato')}: ${
            store.lendingInstallationMinutes ?? '-'
          } min`,
        })
      }

      return buttons
    },

    editMinDeliveryMinutes(store) {
      this.$refs.minDeliveryMinutesModal.show(store)
    },

    async toggleDelivery(store) {
      const willPause = store.delivery === true
      const confirmed = await this.confirm({
        title: willPause ? this.$t('Pausar delivery?') : this.$t('Retomar delivery?'),
        text: willPause
          ? this.$t('O delivery desta loja ficará indisponível até ser retomado.')
          : this.$t('O delivery desta loja voltará a ficar disponível.'),
        confirmButtonText: willPause ? this.$t('Pausar') : this.$t('Retomar'),
      })
      if (!confirmed) return

      try {
        this.fetching = true
        await this.setStoreDeliveryPaused({ storeId: store.id, paused: willPause })
        this.showSuccess({
          message: willPause ? this.$t('Delivery pausado.') : this.$t('Delivery retomado.'),
        })
        await this.getData()
      } catch (error) {
        this.showGenericError({ error })
      } finally {
        this.fetching = false
      }
    },

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchStores()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },
    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },
    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },
    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },
    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },
    create() {
      this.clearStore()
      this.$router.push({ name: 'store-add' })
    },
    edit(store) {
      this.clearStore()
      this.$router.push({
        name: 'store-maintain',
        params: { id: store.id },
      })
    },
  },
}
</script>
